import { Link } from "wouter";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Compass, Shield } from "lucide-react";
import { useSEO } from "@/hooks/use-seo";

export default function About() {
  useSEO({
    title: "About - The Lifecycle Canvas",
    description: "Learn about The Lifecycle Canvas - the interactive planning tool built specifically for Interim Executives navigating career transitions.",
    ogTitle: "About - The Lifecycle Canvas",
    ogDescription: "Learn about the interactive planning tool built for Interim Executives.",
  });

  return (
    <Layout>
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
            About The Lifecycle Canvas
          </h1>
          <p className="text-xl text-slate-400 mb-12">
            The Lifecycle Canvas is an interactive tool designed specifically for Interim Executives who need to navigate the unique challenges of transitioning between assignments.
          </p>

          <div className="space-y-12">
            <div className="flex gap-6">
              <div className="w-12 h-12 bg-teal-500/20 text-teal-400 rounded-lg flex items-center justify-center shrink-0">
                <Users className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Built for Interim Executives</h2>
                <p className="text-slate-400">
                  Unlike generic career planning tools, The Lifecycle Canvas understands the cyclical nature of interim work. We recognize that you're constantly moving between "on assignment" and "off assignment" phases, each with its own challenges and opportunities.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-rose-500/20 text-rose-400 rounded-lg flex items-center justify-center shrink-0">
                <Compass className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Visual Planning</h2>
                <p className="text-slate-400">
                  Our drag-and-drop canvas lets you visualize your journey across time. Map your lifecycle modes, identify potential pain points before they become problems, and strategically place interventions to smooth your transitions.
                </p>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="w-12 h-12 bg-amber-500/20 text-amber-400 rounded-lg flex items-center justify-center shrink-0">
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Scenario Planning</h2>
                <p className="text-slate-400">
                  Whether you're heading to bench, seamlessly transitioning to a new role, or new to interim work entirely, The Lifecycle Canvas lets you plan for multiple scenarios so you're always prepared.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-16 pt-12 border-t border-slate-800 text-center">
            <h3 className="text-2xl font-display font-bold text-white mb-4">
              Ready to Map Your Journey?
            </h3>
            <Link href="/apply">
              <Button size="lg" className="bg-teal-700 hover:bg-teal-600 text-white px-8" data-testid="about-cta">
                Apply for Early Access
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
